import { AlertTriangle, Compass, ListChecks, RefreshCw, TrendingUp } from 'lucide-react';
import { SystemMap } from '../components/overview/SystemMap';
import { AttentionFeed } from '../components/overview/AttentionFeed';
import { QuickLinksPanel } from '../components/overview/QuickLinksPanel';
import { Sparkline } from '../components/overview/Sparkline';
import { ChecklistPanel } from '../components/checks/ChecklistPanel';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { useOverviewSummary, useMetricsHistory } from '../hooks/useOverview';
import { useChecksSummary } from '../hooks/useChecks';
import { getApiErrorMessage } from '../api/client';
import { formatRelativeTime } from '../utils/format';
import { cn } from '../utils/cn';

export function Overview() {
  const { data, isLoading, isFetching, isError, error, refetch } = useOverviewSummary();
  const { data: history } = useMetricsHistory(24 * 7);
  const { data: checks } = useChecksSummary();

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card className="p-8 text-center">
        <AlertTriangle className="mx-auto mb-3 text-rose-500" size={28} />
        <p className="font-medium text-slate-700 dark:text-slate-200">Couldn&apos;t load the overview</p>
        <p className="mt-1 text-sm text-slate-400">{getApiErrorMessage(error)}</p>
      </Card>
    );
  }

  const snapshots = history?.snapshots ?? [];
  const trendSeries = {
    jiraOpen: snapshots.map((s) => s.jira?.openIssues).filter((v): v is number => v !== undefined),
    wazuhDisconnected: snapshots.map((s) => s.wazuh?.disconnectedAgents).filter((v): v is number => v !== undefined),
    githubFailing: snapshots.map((s) => s.github?.failingRuns).filter((v): v is number => v !== undefined),
    dockerRunning: snapshots.map((s) => s.docker?.containersRunning).filter((v): v is number => v !== undefined),
  };
  const hasAnyTrend = Object.values(trendSeries).some((series) => series.length >= 2);

  const criticalCount = data.attentionItems.filter((i) => i.severity === 'critical').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm text-slate-400">
            Live across every connected system &middot; updated {formatRelativeTime(data.fetchedAt)}
          </p>
          {criticalCount > 0 && (
            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
              {criticalCount} need{criticalCount === 1 ? 's' : ''} attention
            </span>
          )}
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 text-sm font-medium text-pulse-600 hover:text-pulse-700 dark:text-pulse-400"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1.5">
            <ListChecks size={14} /> System checks
          </CardTitle>
          {checks && (
            <div className="flex items-center gap-1.5 text-xs font-semibold">
              {checks.failCount > 0 && (
                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                  {checks.failCount} fail
                </span>
              )}
              {checks.warnCount > 0 && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                  {checks.warnCount} warn
                </span>
              )}
              <span className={cn('rounded-full px-2 py-0.5', 'bg-pulse-100 text-pulse-700 dark:bg-pulse-900/40 dark:text-pulse-300')}>
                {checks.passCount} pass
              </span>
            </div>
          )}
        </CardHeader>
        <CardBody>
          {checks ? (
            <>
              <ChecklistPanel results={checks.results} />
              {checks.lastRunAt && (
                <p className="mt-4 text-xs text-slate-400">
                  Checks run every {checks.intervalMinutes} minutes &middot; last run {formatRelativeTime(checks.lastRunAt)}
                </p>
              )}
            </>
          ) : (
            <div className="flex justify-center py-6">
              <Spinner size={20} />
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1.5">
            <Compass size={14} /> System map
          </CardTitle>
        </CardHeader>
        <CardBody>
          <SystemMap systemNodes={data.systemNodes} />
        </CardBody>
      </Card>

      {hasAnyTrend && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-1.5">
              <TrendingUp size={14} /> 7-day trends
            </CardTitle>
          </CardHeader>
          <CardBody className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {trendSeries.jiraOpen.length >= 2 && (
              <Sparkline label="Jira open issues" values={trendSeries.jiraOpen} higherIsWorse />
            )}
            {trendSeries.wazuhDisconnected.length >= 2 && (
              <Sparkline label="Disconnected agents" values={trendSeries.wazuhDisconnected} higherIsWorse color="#e11d48" />
            )}
            {trendSeries.githubFailing.length >= 2 && (
              <Sparkline label="Failing CI runs" values={trendSeries.githubFailing} higherIsWorse color="#e11d48" />
            )}
            {trendSeries.dockerRunning.length >= 2 && (
              <Sparkline label="Containers running" values={trendSeries.dockerRunning} />
            )}
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Needs attention</CardTitle>
          </CardHeader>
          <CardBody>
            <AttentionFeed items={data.attentionItems} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick links</CardTitle>
          </CardHeader>
          <CardBody>
            <QuickLinksPanel links={data.quickLinks} />
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
